import React, { useState } from 'react';
import { Container } from '@fiverr-private/layout_components';
import { Heading, Typography } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { raiseToast } from '@fiverr-private/toast';
import pathfinder from '@fiverr-private/pathfinder';
import { Loader } from '@fiverr-private/feedback';
import { globalMixpanelReporter } from '../../../../../events/mixpanel';
import { globalBigQueryReporter } from '../../../../../events/bigQuery';
import { resetPassword } from '../../../../../services';
import { raiseErrorToast } from '../../../../../utils/raiseErrorToast';
import { translate } from '../../../../../utils/translate';
import { Variant } from '../../../../../shared/constants';
import { OnboardingFooter } from '../../../../OnboardingFooter';

interface ResetPasswordCheckInboxProps {
    email: string;
    handleDoneClick?: () => void;
    variant?: Variant;
    goBack?: () => void;
}

export const CheckInbox = ({ email, handleDoneClick, variant, goBack }: ResetPasswordCheckInboxProps) => {
    const [resendEmailAttempts, setResendEmailAttempts] = useState(1);
    const [isResending, setIsResending] = useState(false);
    const isOnboarding = variant === 'onboarding';

    const handleResendEmail = async () => {
        setIsResending(true);
        globalBigQueryReporter.send('identification.reset_password.resend_email_click');
        globalMixpanelReporter.send('Identification - Reset Password - Resend email', {
            totalAttempts: resendEmailAttempts,
        });

        setResendEmailAttempts((prev) => prev + 1);

        const { statusCode } = await resetPassword({ email, funnel: 'standard' });

        setIsResending(false);

        if (statusCode !== 200) {
            raiseErrorToast(translate('steps.reset_password_success.toast_failure'));
            return;
        }

        raiseToast({
            type: 'success',
            message: translate('steps.reset_password_success.toast_success'),
            location: 'top',
        });
    };

    const handleDone = () => {
        globalBigQueryReporter.send('identification.reset_password.done_click');
        globalMixpanelReporter.send('Identification - Reset Password - Done');

        if (handleDoneClick) {
            handleDoneClick();
            return;
        }

        window.location.assign(pathfinder('homepage'));
    };

    return (
        <Container display="flex" direction="column" flexGrow="1">
            <Heading as={isOnboarding ? 'h3' : 'h4'}>{translate('steps.reset_password_success.title')}</Heading>

            <Typography marginTop="2" marginBottom="5" color="grey_1000">
                {translate('steps.reset_password_success.subtitle')}
            </Typography>

            <Container
                marginTop={isOnboarding ? undefined : { default: 'auto', md: '0' }}
                display="flex"
                direction="column"
                alignItems="stretch"
            >
                <Button type="submit" onClick={handleDone}>
                    {translate('steps.reset_password_success.done')}
                </Button>
            </Container>

            <Container color="grey_1000" marginTop="6">
                {translate('steps.reset_password_success.resend_email', {
                    templates: {
                        resendEmailBtn: (text: string) => (
                            <Container display="flex" alignItems="center" gap="2">
                                <Typography
                                    as="span"
                                    decoration="underline"
                                    cursor="pointer"
                                    color="grey_1200"
                                    onClick={handleResendEmail}
                                    disabled={isResending}
                                >
                                    {text}
                                </Typography>

                                {isResending && <Loader size="sm" />}
                            </Container>
                        ),
                    },
                })}
            </Container>

            {isOnboarding && goBack && (
                <Container marginTop="auto">
                    <OnboardingFooter goBack={goBack}></OnboardingFooter>
                </Container>
            )}
        </Container>
    );
};
